import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    MapPin,
    Phone,
    Mail,
    Clock,
    Facebook,
    Twitter,
    Youtube,
} from 'lucide-react';

const pageHref = (page) => {
    if (page.externalUrl) return page.externalUrl;
    if (page.url) return page.url.startsWith('/') ? page.url : '/' + page.url;
    return `/content/${page.id}`;
};

const pickName = (page, lang) => {
    if (lang === 'en' && page.nameEn) return page.nameEn;
    return page.name || '';
};

export default function Footer() {
    const { t } = useTranslation();
    const lang = useSelector((s) => s.system.lang || 'mn');
    const footerMenus = useSelector((s) => s.system.footerMenus || []);
    const companyInfo = useSelector((s) => s.system.companyInfo || {});

    // 3 баганаар бүлэглэх (footer_column field-аар, эсвэл байхгүй бол position-аар)
    const grouped = { 1: [], 2: [], 3: [] };
    footerMenus.forEach((p) => {
        const col = p.footerColumn && p.footerColumn >= 1 && p.footerColumn <= 3 ? p.footerColumn : 1;
        grouped[col].push(p);
    });

    return (
        <footer className="bg-brand-dark text-white/80">
            {/* ─── Main grid ───────────────────────────────────── */}
            <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* ── Company block ── */}
                    <div className="lg:col-span-1">
                        <Image
                            src="/logo.png"
                            alt="Mongol Refinery"
                            width={200}
                            height={70}
                            className="mb-5"
                        />
                        <p className="text-sm leading-relaxed text-white/60 mb-5">
                            {lang === 'en'
                                ? 'Mongol Refinery State Owned LLC was established in April 2017 to implement the oil refinery construction project funded by the Government of India.'
                                : '"Монгол газрын тос боловсруулах үйлдвэр" ТӨХХК нь Бүгд Найрамдах Энэтхэг Улсын Засгийн газрын зээлийн хөрөнгөөр газрын тос боловсруулах үйлдвэр байгуулах төслийг хэрэгжүүлэх үүрэгтэй төрийн өмчит компани юм.'}
                        </p>
                        {/* Social */}
                        <div className="flex gap-3">
                            {companyInfo.facebook && (
                                <a
                                    href={companyInfo.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Facebook"
                                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-brand-yellow hover:text-brand-dark border border-white/10 rounded transition"
                                >
                                    <Facebook size={16} />
                                </a>
                            )}
                            {companyInfo.twitter && (
                                <a
                                    href={companyInfo.twitter}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Twitter"
                                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-brand-yellow hover:text-brand-dark border border-white/10 rounded transition"
                                >
                                    <Twitter size={16} />
                                </a>
                            )}
                            {companyInfo.youtube && (
                                <a
                                    href={companyInfo.youtube}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="YouTube"
                                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-brand-yellow hover:text-brand-dark border border-white/10 rounded transition"
                                >
                                    <Youtube size={16} />
                                </a>
                            )}
                        </div>
                    </div>

                    {/* ── Dynamic page columns (footer pages) ── */}
                    {[1, 2].map((col) => (
                        <div key={col}>
                            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">
                                {col === 1 ? t('footer.quickLinks') : t('menu.transparency')}
                            </h4>
                            <ul className="space-y-2.5">
                                {(grouped[col].length > 0
                                    ? grouped[col]
                                    : footerMenus.slice(
                                          (col - 1) * 5,
                                          col * 5
                                      )
                                ).map((p) =>
                                    p.externalUrl ? (
                                        <li key={p.id}>
                                            <a
                                                href={pageHref(p)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-white/60 hover:text-brand-yellow transition flex items-center gap-1"
                                            >
                                                {pickName(p, lang)}
                                                <span className="text-[10px] opacity-50">↗</span>
                                            </a>
                                        </li>
                                    ) : (
                                        <li key={p.id}>
                                            <Link
                                                href={pageHref(p)}
                                                className="text-sm text-white/60 hover:text-brand-yellow transition"
                                            >
                                                {pickName(p, lang)}
                                            </Link>
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                    ))}

                    {/* ── Contact info column ── */}
                    <div>
                        <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5">
                            {t('footer.contactInfo')}
                        </h4>
                        <ul className="space-y-3 text-sm text-white/60">
                            {companyInfo.address && (
                                <li className="flex gap-3">
                                    <MapPin
                                        size={16}
                                        className="text-brand-yellow flex-shrink-0 mt-0.5"
                                    />
                                    <span className="leading-relaxed">
                                        {companyInfo.address}
                                    </span>
                                </li>
                            )}
                            {companyInfo.phone && (
                                <li>
                                    <a
                                        href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                                        className="flex gap-3 hover:text-brand-yellow transition"
                                    >
                                        <Phone
                                            size={16}
                                            className="text-brand-yellow flex-shrink-0 mt-0.5"
                                        />
                                        {companyInfo.phone}
                                    </a>
                                </li>
                            )}
                            {companyInfo.email && (
                                <li>
                                    <a
                                        href={`mailto:${companyInfo.email}`}
                                        className="flex gap-3 hover:text-brand-yellow transition"
                                    >
                                        <Mail
                                            size={16}
                                            className="text-brand-yellow flex-shrink-0 mt-0.5"
                                        />
                                        {companyInfo.email}
                                    </a>
                                </li>
                            )}
                            {companyInfo.working_hours && (
                                <li className="flex gap-3">
                                    <Clock
                                        size={16}
                                        className="text-brand-yellow flex-shrink-0 mt-0.5"
                                    />
                                    {companyInfo.working_hours}
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            {/* ─── Copyright bar ───────────────────────────────── */}
            <div className="border-t border-white/10">
                <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40">
                    <p>{t('footer.copyright')}</p>
                    <a
                        href="https://alphafox.mn/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-2 hover:text-white transition"
                        aria-label="Alpha Fox Solution"
                    >
                        <span>
                            {lang === 'en' ? 'Developed by' : 'Хөгжүүлсэн:'}
                        </span>
                        <Image
                            src="/global/alphafox-logo.png"
                            alt="Alpha Fox Solution"
                            width={20}
                            height={20}
                            className="object-contain opacity-60 group-hover:opacity-100 transition"
                        />
                        <span className="font-semibold tracking-wide">
                            {lang === 'en'
                                ? 'Alpha Fox Solution LLC'
                                : 'Альфа Фокс Солюшн ХХК'}
                        </span>
                    </a>
                </div>
            </div>
        </footer>
    );
}